import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  TextField,
  Button,
  Tooltip,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Popover,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGridPro } from '@mui/x-data-grid-pro';
import '../App.css';

// Deeplink supported sportsbooks
const DEEPLINK_SUPPORTED_BOOKS = [
  'fanduel',
  'draftkings',
  'betmgm',
  'caesars',
  'espn bet', 
  'hard rock',
  'novig',
  'fanatics',
  'prophet exchange'
];

// Custom dark theme (same as EVTool.js)
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
  },
});

// Helper function to convert American odds to decimal
const americanToDecimal = (american) => {
  if (american > 0) {
    return (american / 100) + 1;
  } else {
    return (100 / Math.abs(american)) + 1;
  }
};

// Helper function to calculate profit from stakes and odds
const calculateProfit = (stake1, stake2, price1, price2) => {
  const decimal1 = americanToDecimal(price1);
  const decimal2 = americanToDecimal(price2);
  const payout1 = stake1 * decimal1;
  const payout2 = stake2 * decimal2;
  const totalStake = stake1 + stake2;
  // Both payouts should be approximately equal in a true arbitrage
  return Math.min(payout1, payout2) - totalStake;
};

// DeeplinkCell component with dynamic fetching (matches EVTool.js)
const DeeplinkCell = React.memo(({ marketLineId, points, marketSourceName, fetchDeeplink, isAuthenticated }) => {
  const [loading, setLoading] = useState(false);
  
  // Check if this book supports deeplinks
  const bookSupportsDeeplink = DEEPLINK_SUPPORTED_BOOKS.includes(
    marketSourceName?.toLowerCase()
  );
  
  const handleBetClick = async () => {
    if (!isAuthenticated || !marketLineId || !bookSupportsDeeplink) {
      return;
    }
    
    setLoading(true);
    
    try {
      const deeplink = await fetchDeeplink(marketLineId, points, marketSourceName);
      
      if (deeplink) {
        window.open(deeplink, '_blank', 'width=1000,height=800,scrollbars=yes');
      }
    } catch (error) {
      console.error('Error fetching deeplink:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const disabled = !marketLineId || !isAuthenticated || !bookSupportsDeeplink || loading;
  
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Button
        onClick={handleBetClick}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? '#666666' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: '#666666' },
        }}
      >
        {loading ? '...' : 'Bet'}
      </Button>
    </Box>
  );
});

// Hide Arbitrage Cell Component
const HideArbCell = React.memo(({ row, hiddenArbs, toggleHide, showHiddenArbs, isAuthenticated }) => {
  const id = row.id;
  const isHidden = hiddenArbs.includes(id);
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleHide(row);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenArbs) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

// Track Arbitrage Cell Component
const TrackArbCell = React.memo(({ row, trackedBets, toggleTrack, trackBet, untrackBet, trackingInProgress, isAuthenticated }) => {
  // Check if bets are tracked using real odds IDs
  const bet1Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === row.oddsId1 || bet.id === row.oddsId1
  );
  const bet2Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === row.oddsId2 || bet.id === row.oddsId2
  );
  // Show as tracked if both bets are tracked; partial tracking will show as untracked but enable action
  const isFullyTracked = bet1Tracked && bet2Tracked;
  
  const isDisabled = !trackBet || !untrackBet || trackingInProgress.has(row.id) || !isAuthenticated;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleTrack(row, trackBet, untrackBet, bet1Tracked, bet2Tracked);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isFullyTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isFullyTracked ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
          </svg>
        )}
      </IconButton>
    </Box>
  );
});

// Pin Arbitrage Cell Component
const PinArbCell = React.memo(({ row, pinnedArbs, togglePin, createPinKey, isAuthenticated }) => {
  const pinKey = createPinKey(row);
  const isPinned = pinnedArbs.includes(pinKey);
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            togglePin(row);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isPinned ? '#ff1744' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isPinned ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,4V9C14,10.12 14.37,11.16 15,12H9C9.65,11.14 10,10.1 10,9V4H14M16,4H17V2H7V4H8V9C8,10.1 7.65,11.16 7,12V14H11V22H13V14H17V12C16.36,11.16 16,10.12 16,9V4Z" opacity="0.5" />
          </svg>
        )}
      </IconButton>
    </Box>
  );
});

// Editable Stake Cell Component
const EditableStakeCell = React.memo(({ value, rowId, field, onStakeChange }) => {
  const [localValue, setLocalValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const handleFocus = (e) => {
    e.target.select();
    setLocalValue('');
  };

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue >= 0) {
      // Round to nearest cent
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(rowId, field, roundedValue);
    } else {
      // Revert to original value if empty or invalid
      setLocalValue(value.toFixed(2));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        size="small"
        type="number"
        inputProps={{ 
          min: 0, 
          step: 0.01,
          style: { 
            MozAppearance: 'textfield',
            WebkitAppearance: 'none',
            appearance: 'none'
          }
        }}
        autoFocus
        sx={{
          width: '85px',
          '& .MuiInputBase-input': {
            padding: '4px 8px',
            fontSize: '14px',
            color: '#ffffff',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
      />
    );
  }

  return (
    <Box
      onClick={() => setIsEditing(true)}
      sx={{
        cursor: 'pointer',
        '&:hover': { 
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '85px',
      }}
    >
      <Typography variant="body2" sx={{ color: '#ffffff' }}>
        ${value.toFixed(2)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px' }}>
        ✎
      </Typography>
    </Box>
  );
});

// Mobile Arbitrage Card Component with editable stakes
const MobileArbCard = React.memo(({ 
  arb, defaultStake, onStakeChange, trackedBets, trackBet, untrackBet, 
  trackingInProgress, isAuthenticated, hiddenArbs, toggleHide, showHiddenArbs, 
  pinnedArbs, togglePin, createPinKey, fetchDeeplink 
}) => {
  // Check if bets are tracked using real odds IDs
  const bet1Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === arb.oddsId1 || bet.id === arb.oddsId1
  );
  const bet2Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === arb.oddsId2 || bet.id === arb.oddsId2
  );
  // Show as tracked only if both bets are tracked
  const isTracked = bet1Tracked && bet2Tracked;
  const isTrackingDisabled = !trackBet || !untrackBet || trackingInProgress.has(arb.id) || !isAuthenticated;
  const isHidden = hiddenArbs.includes(arb.id);
  const isHideDisabled = (isHidden && !showHiddenArbs) || !isAuthenticated;
  const pinKey = createPinKey(arb);
  const isPinned = pinnedArbs.includes(pinKey);

  // Parse event info
  const [awayTeam, homeTeam] = (arb.eventShortName || '').split('@');
  const eventDate = arb.eventStart
    ? new Date(arb.eventStart).toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '')
    : '';
  
  // Use stakes from arb object (which may have been manually adjusted)
  const stake1 = arb.stake1;
  const stake2 = arb.stake2;
  const guaranteedProfit = arb.actualProfit;
  const totalStake = stake1 + stake2;
  const roi = totalStake > 0 ? (guaranteedProfit / totalStake) * 100 : 0;
  
  // Conditional formatting for ROI
  let roiColor = '#ffffff';
  if (roi >= 10) {
    roiColor = '#00ff00'; // Bright green for 10%+ ROI
  } else if (roi >= 5) {
    roiColor = '#66ff66'; // Medium green for 5-10% ROI
  } else if (roi >= 2) {
    roiColor = '#99ff99'; // Light green for 2-5% ROI
  } else if (roi >= 1) {
    roiColor = '#ccffcc'; // Very light green for 1-2% ROI
  }
  
  // Format lines
  const formatLine = (side, points) => {
    const sideAbbr = side === 'Over' ? 'o' : side === 'Under' ? 'u' : '';
    return `${sideAbbr}${points || ''}`.trim();
  };
  
  const line1 = formatLine(arb.side1, arb.points1);
  const line2 = formatLine(arb.side2, arb.points1);

  const [editingStake1, setEditingStake1] = useState(false);
  const [editingStake2, setEditingStake2] = useState(false);
  const [tempStake1, setTempStake1] = useState('');
  const [tempStake2, setTempStake2] = useState('');
  const [loadingBet1, setLoadingBet1] = useState(false);
  const [loadingBet2, setLoadingBet2] = useState(false);

  const handleStake1Focus = (e) => {
    e.target.select();
    setTempStake1('');
  };

  const handleStake2Focus = (e) => {
    e.target.select();
    setTempStake2('');
  };

  const handleStake1Change = () => {
    setEditingStake1(false);
    const numValue = parseFloat(tempStake1);
    if (!isNaN(numValue) && numValue >= 0) {
      // Round to nearest cent
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(arb.id, 'stake1', roundedValue);
    }
    // Reset temp value for next edit
    setTempStake1('');
  };

  const handleStake2Change = () => {
    setEditingStake2(false);
    const numValue = parseFloat(tempStake2);
    if (!isNaN(numValue) && numValue >= 0) {
      // Round to nearest cent
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(arb.id, 'stake2', roundedValue);
    }
    // Reset temp value for next edit
    setTempStake2('');
  };

  const bookSupportsDeeplink1 = DEEPLINK_SUPPORTED_BOOKS.includes(arb.ms1?.toLowerCase());
  const bookSupportsDeeplink2 = DEEPLINK_SUPPORTED_BOOKS.includes(arb.ms2?.toLowerCase());
  
  return (
    <Card sx={{ 
      mb: 2, 
      border: isPinned ? '2px solid #ff1744' : '1px solid #333',
      backgroundColor: '#1e1e1e',
      borderRadius: 2,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Profit Header - Always visible */}
      <Box sx={{ 
        backgroundColor: '#2a2a2a',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #333'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ color: '#ffffff' }}>
              ${guaranteedProfit.toFixed(2)} Profit
            </Typography>
            <Chip 
              label={`${roi.toFixed(1)}% ROI`}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: roiColor,
                fontWeight: 500
              }}
            />
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => !isAuthenticated ? null : togglePin(arb)}
              disabled={!isAuthenticated}
              sx={{ 
                color: isPinned ? '#ff1744' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isPinned ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,4V9C14,10.12 14.37,11.16 15,12H9C9.65,11.14 10,10.1 10,9V4H14M16,4H17V2H7V4H8V9C8,10.1 7.65,11.16 7,12V14H11V22H13V14H17V12C16.36,11.16 16,10.12 16,9V4Z" opacity="0.5" />
                </svg>
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                if (!isTrackingDisabled) {
                  const toggleTrackFunc = () => {
                    if (isTracked) {
                      // Untrack both bets if both are tracked
                      if (bet1Tracked && arb.oddsId1) untrackBet(arb.oddsId1);
                      if (bet2Tracked && arb.oddsId2) untrackBet(arb.oddsId2);
                    } else {
                      let sidepoints1 = '';
                      if (arb.side1 === 'Over') sidepoints1 = 'o';
                      else if (arb.side1 === 'Under') sidepoints1 = 'u';
                      else sidepoints1 = arb.side1 || '';
                      sidepoints1 = `${sidepoints1}${arb.points1 || ''}`.trim();

                      let sidepoints2 = '';
                      if (arb.side2 === 'Over') sidepoints2 = 'o';
                      else if (arb.side2 === 'Under') sidepoints2 = 'u';
                      else sidepoints2 = arb.side2 || '';
                      sidepoints2 = `${sidepoints2}${arb.points1 || ''}`.trim();
                      // Track only untracked bets
                      const bet1 = {
                        id: arb.oddsId1 || `${arb.id}_bet1`,
                        oddsId: arb.oddsId1,
                        marketSourceName: arb.ms1,
                        eventShortName: arb.eventShortName,
                        eventStart: arb.eventStart,
                        playerName: arb.playerName,
                        betTypeName: arb.betTypeName,
                        side: arb.side1,
                        points: arb.points1,
                        price: arb.price1,
                        breakEvenWin: arb.breakEvenWin1,
                        roi: arb.roi1,
                        marketLineId: arb.marketLineId1,
                        stake: arb.stake1,
                        logourl: arb.logo1,
                        awayLogo: arb.awayLogo,
                        homeLogo: arb.homeLogo,
                        leagueName: arb.name,
                        betCategory: arb.betCategory,
                        betMarketId: arb.betMarketId,
                        sidepoints: sidepoints1
                      };
                      
                      const bet2 = {
                        id: arb.oddsId2 || `${arb.id}_bet2`,
                        oddsId: arb.oddsId2,
                        marketSourceName: arb.ms2,
                        eventShortName: arb.eventShortName,
                        eventStart: arb.eventStart,
                        playerName: arb.playerName,
                        betTypeName: arb.betTypeName,
                        side: arb.side2,
                        points: arb.points1,
                        price: arb.price2,
                        breakEvenWin: arb.breakEvenWin2,
                        roi: arb.roi2,
                        marketLineId: arb.marketLineId2,
                        stake: arb.stake2,
                        logourl: arb.logo2,
                        awayLogo: arb.awayLogo,
                        homeLogo: arb.homeLogo,
                        leagueName: arb.name,
                        betCategory: arb.betCategory,
                        betMarketId: arb.betMarketId,
                        sidepoints: sidepoints2
                      };
                      
                      if (!bet1Tracked && arb.oddsId1) trackBet(bet1);
                      if (!bet2Tracked && arb.oddsId2) trackBet(bet2);
                    }
                  };
                  toggleTrackFunc();
                }
              }}
              disabled={isTrackingDisabled}
              sx={{ 
                color: isTracked ? '#00ff00' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isTracked ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                </svg>
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => !isHideDisabled && toggleHide(arb)}
              disabled={isHideDisabled}
              sx={{ 
                color: isHidden ? '#00ff00' : '#888',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: '#444',
                }
              }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>
      
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Player and Bet Type */}
        {(arb.playerName || arb.betTypeName) && (
          <Box mb={2}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {arb.playerName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#BBBBBB' }}>
              {arb.betTypeName}
            </Typography>
          </Box>
        )}
        
        {/* Event Info */}
        <Box mb={2}>
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            {arb.name && (
              <>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {arb.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  |
                </Typography>
              </>
            )}
            {arb.awayLogo && (
              <img
                src={arb.awayLogo}
                alt={awayTeam}
                style={{ width: 'auto', height: '16px', objectFit: 'contain' }}
                loading="lazy"
              />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {awayTeam}
            </Typography>
            <Typography variant="body2" sx={{ mx: 0.5, color: '#ffffff' }}>
              @
            </Typography>
            {arb.homeLogo && (
              <img
                src={arb.homeLogo}
                alt={homeTeam}
                style={{ width: 'auto', height: '16px', objectFit: 'contain' }}
                loading="lazy"
              />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {homeTeam}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: '#BBBBBB' }}>
            {eventDate}
          </Typography>
        </Box>
        
        {/* Two Bets Side by Side */}
        <Stack spacing={2}>
          {/* Bet 1 */}
          <Box sx={{ 
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444'
          }}>
            {/* Top row: Logo and Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <img
                src={arb.logo1}
                alt={arb.ms1}
                style={{ width: 60, height: 30, objectFit: 'contain' }}
                loading="lazy"
              />
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  if (arb.marketLineId1 && isAuthenticated && bookSupportsDeeplink1) {
                    setLoadingBet1(true);
                    try {
                      const deeplink = await fetchDeeplink(arb.marketLineId1, arb.points1, arb.ms1);
                      if (deeplink) window.open(deeplink, '_blank');
                    } catch (error) {
                      console.error('Error fetching deeplink:', error);
                    } finally {
                      setLoadingBet1(false);
                    }
                  }
                }}
                disabled={!arb.marketLineId1 || !isAuthenticated || !bookSupportsDeeplink1 || loadingBet1}
                sx={{
                  backgroundColor: bookSupportsDeeplink1 && isAuthenticated ? '#00ff00' : '#333',
                  color: bookSupportsDeeplink1 && isAuthenticated ? '#000' : '#666',
                  minWidth: 70,
                  '&:hover': { backgroundColor: bookSupportsDeeplink1 && isAuthenticated ? '#00cc00' : '#333' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                {loadingBet1 ? '...' : 'Bet'}
              </Button>
            </Stack>
            
            {/* Line/Odds */}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line1} {arb.price1 < 0 ? arb.price1 : `+${arb.price1}`}
            </Typography>
            
            {/* Stake */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#ffffff' }}>
                Stake: $
              </Typography>
              {editingStake1 ? (
                <TextField
                  value={tempStake1}
                  onChange={(e) => setTempStake1(e.target.value)}
                  onBlur={handleStake1Change}
                  onKeyPress={(e) => e.key === 'Enter' && handleStake1Change()}
                  onFocus={handleStake1Focus}
                  size="small"
                  type="number"
                  inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    style: { 
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }
                  }}
                  autoFocus
                  sx={{
                    width: '70px',
                    '& .MuiInputBase-input': {
                      padding: '2px 6px',
                      fontSize: '12px',
                      color: '#ffffff',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              ) : (
                <Box
                  onClick={() => setEditingStake1(true)}
                  sx={{ 
                    color: '#ffffff',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    '&:hover': { 
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#ffffff' }}>
                    {stake1.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>
                    ✎
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Bet 2 */}
          <Box sx={{ 
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444'
          }}>
            {/* Top row: Logo and Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <img
                src={arb.logo2}
                alt={arb.ms2}
                style={{ width: 60, height: 30, objectFit: 'contain' }}
                loading="lazy"
              />
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  if (arb.marketLineId2 && isAuthenticated && bookSupportsDeeplink2) {
                    setLoadingBet2(true);
                    try {
                      const deeplink = await fetchDeeplink(arb.marketLineId2, arb.points1, arb.ms2);
                      if (deeplink) window.open(deeplink, '_blank');
                    } catch (error) {
                      console.error('Error fetching deeplink:', error);
                    } finally {
                      setLoadingBet2(false);
                    }
                  }
                }}
                disabled={!arb.marketLineId2 || !isAuthenticated || !bookSupportsDeeplink2 || loadingBet2}
                sx={{
                  backgroundColor: bookSupportsDeeplink2 && isAuthenticated ? '#00ff00' : '#333',
                  color: bookSupportsDeeplink2 && isAuthenticated ? '#000' : '#666',
                  minWidth: 70,
                  '&:hover': { backgroundColor: bookSupportsDeeplink2 && isAuthenticated ? '#00cc00' : '#333' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                {loadingBet2 ? '...' : 'Bet'}
              </Button>
            </Stack>
            
            {/* Line/Odds */}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line2} {arb.price2 < 0 ? arb.price2 : `+${arb.price2}`}
            </Typography>
            
            {/* Stake */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#ffffff' }}>
                Stake: $
              </Typography>
              {editingStake2 ? (
                <TextField
                  value={tempStake2}
                  onChange={(e) => setTempStake2(e.target.value)}
                  onBlur={handleStake2Change}
                  onKeyPress={(e) => e.key === 'Enter' && handleStake2Change()}
                  onFocus={handleStake2Focus}
                  size="small"
                  type="number"
                  inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    style: { 
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }
                  }}
                  autoFocus
                  sx={{
                    width: '70px',
                    '& .MuiInputBase-input': {
                      padding: '2px 6px',
                      fontSize: '12px',
                      color: '#ffffff',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              ) : (
                <Box
                  onClick={() => setEditingStake2(true)}
                  sx={{ 
                    color: '#ffffff',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    '&:hover': { 
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#ffffff' }}>
                    {stake2.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>
                    ✎
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

const ArbDataGrid = ({ arbitrages, updatesPaused, trackBet, untrackBet, trackedBets = [], isAuthenticated = true, userState = '' }) => {
  // State for filters and total stake
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  // Add the fetchDeeplink function
  const fetchDeeplink = useCallback(async (marketLineId, points, marketSourceName) => {
    try {
      const response = await fetch('https://ws.betpros.ai:8080/api/deeplink', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          marketLineId,
          points,
          marketSourceName,
          isMobile: screenWidth < 768,
          userState: userState || ''
        })
      });
      
      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error fetching deeplink:', error);
      return null;
    }
  }, [screenWidth, userState]);
  
  // Load saved filters and stake from localStorage
  const savedLeagueFilter = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbLeagueFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  const savedBookFilter = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbBookFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  const savedDefaultStake = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbDefaultStake');
      return saved ? parseFloat(saved) : 1000;
    } catch {
      return 1000;
    }
  }, []);

  const savedHideTracked = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbHideTracked');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  }, []);

  const savedShowHiddenArbs = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbShowHiddenArbs');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  }, []);

  const [leagueFilter, setLeagueFilter] = useState(savedLeagueFilter);
  const [bookFilter, setBookFilter] = useState(savedBookFilter);
  const leagueInitialized = useRef(false);
  const bookInitialized = useRef(false);
  const [defaultStake, setDefaultStake] = useState(savedDefaultStake);
  const [currentPage, setCurrentPage] = useState(0);
  const [manualStakes, setManualStakes] = useState({});
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [hideTracked, setHideTracked] = useState(savedHideTracked);
  const [showHiddenArbs, setShowHiddenArbs] = useState(savedShowHiddenArbs);
  const [hiddenArbs, setHiddenArbs] = useState(() => {
    const saved = localStorage.getItem('hiddenArbs');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.slice(-1000); // Keep last 1000 hidden arbs
  });
  const [pinnedArbs, setPinnedArbs] = useState(() => {
    const saved = localStorage.getItem('pinnedArbs');
    const parsed = saved ? JSON.parse(saved) : [];
    // Ensure all items are strings
    const validPins = parsed.filter(item => typeof item === 'string');
    return validPins.slice(-100); // Keep last 100 pinned arbs
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  // Create stable composite key for pinning
  const createPinKey = useCallback((arb) => {
    // Create a composite key from: playerName, betTypeName, book1, book2, and line details
    const playerName = arb.playerName || '';
    const betTypeName = arb.betTypeName || '';
    const book1 = arb.ms1 || '';
    const book2 = arb.ms2 || '';
    const side1 = arb.side1 || '';
    const side2 = arb.side2 || '';
    const points = arb.points1 || '';
    
    // Create a normalized line representation
    const line = `${side1}-${side2}-${points}`.toLowerCase();
    
    // Combine all parts into a stable key
    return `${playerName}|${betTypeName}|${book1}|${book2}|${line}`;
  }, []);

  // Migrate old ID-based pins to composite keys on first load
  useEffect(() => {
    if (arbitrages && arbitrages.length > 0 && pinnedArbs.length > 0) {
      // Ensure all items are strings and check if we have old ID-based pins
      const validPins = pinnedArbs.filter(pin => typeof pin === 'string');
      const hasOldFormat = validPins.some(pin => !pin.includes('|'));
      
      // If we filtered out non-strings or need to migrate, update
      if (validPins.length !== pinnedArbs.length || hasOldFormat) {
        // Create a map of old IDs to new composite keys
        const idToKeyMap = new Map(
          arbitrages.map(arb => [arb.id, createPinKey(arb)])
        );
        
        // Convert old pins to new format
        const migratedPins = validPins
          .map(pin => {
            if (pin.includes('|')) {
              // Already in new format
              return pin;
            } else {
              // Old format - try to convert
              return idToKeyMap.get(pin);
            }
          })
          .filter(Boolean); // Remove any undefined values
        
        setPinnedArbs(migratedPins);
        localStorage.setItem('pinnedArbs', JSON.stringify(migratedPins));
      }
    }
  }, [arbitrages]); // Remove pinnedArbs and createPinKey from dependencies to avoid infinite loop

  // Toggle track function for arbitrage (tracks untracked bet or untracks both if both tracked)
  const toggleTrack = useCallback((row, trackBetFunc, untrackBetFunc, bet1Tracked, bet2Tracked) => {
    if (!isAuthenticated || !trackBetFunc || !untrackBetFunc) {
      return;
    }
    
    const arbId = row.id;
    
    // Prevent multiple simultaneous tracking actions
    if (trackingInProgress.has(arbId)) {
      return;
    }

    // Set tracking in progress
    setTrackingInProgress(prev => new Set(prev).add(arbId));

    // If both bets are tracked, untrack both
    if (bet1Tracked && bet2Tracked) {
      if (row.oddsId1) untrackBetFunc(row.oddsId1);
      if (row.oddsId2) untrackBetFunc(row.oddsId2);
    } else {
      // Track only the untracked bet(s)
      let sidepoints1 = '';
      if (row.side1 === 'Over') sidepoints1 = 'o';
      else if (row.side1 === 'Under') sidepoints1 = 'u';
      else sidepoints1 = row.side1 || '';
      sidepoints1 = `${sidepoints1}${row.points1 || ''}`.trim();

      let sidepoints2 = '';
      if (row.side2 === 'Over') sidepoints2 = 'o';
      else if (row.side2 === 'Under') sidepoints2 = 'u';
      else sidepoints2 = row.side2 || '';
      sidepoints2 = `${sidepoints2}${row.points1 || ''}`.trim();
      const bet1 = {
        id: row.oddsId1,
        oddsId: row.oddsId1,
        marketSourceName: row.ms1,
        eventShortName: row.eventShortName,
        eventStart: row.eventStart,
        playerName: row.playerName,
        betTypeName: row.betTypeName,
        side: row.side1,
        points: row.points1,
        price: row.price1,
        breakEvenWin: row.breakEvenWin1,
        roi: row.roi1,
        marketLineId: row.marketLineId1,
        stake: row.stake1,
        logourl: row.logo1,
        awayLogo: row.awayLogo,
        homeLogo: row.homeLogo,
        leagueName: row.name,
        betCategory: row.betCategory,
        betMarketId: row.betMarketId,
        teamName: row.teamName,
        sidepoints: sidepoints1
      };
      
      const bet2 = {
        id: row.oddsId2,
        oddsId: row.oddsId2,
        marketSourceName: row.ms2,
        eventShortName: row.eventShortName,
        eventStart: row.eventStart,
        playerName: row.playerName,
        betTypeName: row.betTypeName,
        side: row.side2,
        points: row.points1,
        price: row.price2,
        breakEvenWin: row.breakEvenWin2,
        roi: row.roi2,
        marketLineId: row.marketLineId2,
        stake: row.stake2,
        logourl: row.logo2,
        awayLogo: row.awayLogo,
        homeLogo: row.homeLogo,
        leagueName: row.name,
        betCategory: row.betCategory,
        betMarketId: row.betMarketId,
        teamName: row.teamName,
        sidepoints: sidepoints2
      };
      
      if (!bet1Tracked && row.oddsId1) {
        trackBetFunc(bet1);
      }
      if (!bet2Tracked && row.oddsId2) {
        trackBetFunc(bet2);
      }
    }

    // Clear tracking progress after a delay
    setTimeout(() => {
      setTrackingInProgress(prev => {
        const next = new Set(prev);
        next.delete(arbId);
        return next;
      });
    }, 500);
  }, [trackedBets, isAuthenticated]);

  // Toggle hide function for arbitrage
  const toggleHide = useCallback((row) => {
    if (!isAuthenticated) {
      return;
    }
    
    const id = row.id;
    const isHidden = hiddenArbs.includes(id);
    
    const updatedHiddenArbs = isHidden
      ? hiddenArbs.filter(x => x !== id)
      : [...hiddenArbs, id];
    
    setHiddenArbs(updatedHiddenArbs);
    localStorage.setItem('hiddenArbs', JSON.stringify(updatedHiddenArbs));
  }, [hiddenArbs, isAuthenticated]);

  // Toggle pin function for arbitrage
  const togglePin = useCallback((row) => {
    if (!isAuthenticated) {
      return;
    }
    
    const pinKey = createPinKey(row);
    const isPinned = pinnedArbs.includes(pinKey);
    
    const updatedPinnedArbs = isPinned
      ? pinnedArbs.filter(x => x !== pinKey)
      : [...pinnedArbs.filter(x => typeof x === 'string'), pinKey]; // Ensure we only keep strings
    
    setPinnedArbs(updatedPinnedArbs);
    localStorage.setItem('pinnedArbs', JSON.stringify(updatedPinnedArbs));
  }, [pinnedArbs, isAuthenticated, createPinKey]);

  // Save filters and settings to localStorage
  useEffect(() => {
    localStorage.setItem('arbLeagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  useEffect(() => {
    localStorage.setItem('arbBookFilter', JSON.stringify(bookFilter));
  }, [bookFilter]);

  useEffect(() => {
    localStorage.setItem('arbDefaultStake', defaultStake.toString());
  }, [defaultStake]);

  useEffect(() => {
    localStorage.setItem('arbHideTracked', JSON.stringify(hideTracked));
  }, [hideTracked]);

  useEffect(() => {
    localStorage.setItem('arbShowHiddenArbs', JSON.stringify(showHiddenArbs));
  }, [showHiddenArbs]);

  useEffect(() => {
    localStorage.setItem('pinnedArbs', JSON.stringify(pinnedArbs));
  }, [pinnedArbs]);

  // Clean up pinnedArbs to remove keys that no longer exist in the dataset
  useEffect(() => {
    if (arbitrages && arbitrages.length > 0) {
      const currentKeys = new Set(arbitrages.map(arb => createPinKey(arb)));
      setPinnedArbs(prev => {
        // Ensure all items are strings before filtering
        const validPins = prev.filter(item => typeof item === 'string');
        const validPinnedArbs = validPins.filter(key => currentKeys.has(key));
        // Only return new array if there's a difference
        return validPinnedArbs.length !== prev.length ? validPinnedArbs : prev;
      });
    }
  }, [arbitrages, createPinKey]);

  // Clean up hiddenArbs to remove IDs that no longer exist in the dataset
  useEffect(() => {
    if (arbitrages && arbitrages.length > 0) {
      const currentIds = new Set(arbitrages.map(arb => arb.id));
      setHiddenArbs(prev => {
        const validHiddenArbs = prev.filter(id => currentIds.has(id));
        // Only return new array if there's a difference
        return validHiddenArbs.length !== prev.length ? validHiddenArbs : prev;
      });
    }
  }, [arbitrages]);

  // Check if mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle stake changes
  const handleStakeChange = (rowId, field, value) => {
    const arb = filteredArbitrages.find(a => a.id === rowId);
    if (!arb) return;

    const decimal1 = americanToDecimal(arb.price1);
    const decimal2 = americanToDecimal(arb.price2);

    let newStake1, newStake2;

    if (field === 'stake1') {
      newStake1 = value;
      // Calculate stake2 to maintain arbitrage ratio
      newStake2 = value * (decimal1 / decimal2);
    } else {
      newStake2 = value;
      // Calculate stake1 to maintain arbitrage ratio
      newStake1 = value * (decimal2 / decimal1);
    }

    // Round both stakes to nearest cent
    newStake1 = Math.round(newStake1 * 100) / 100;
    newStake2 = Math.round(newStake2 * 100) / 100;

    setManualStakes(prev => ({
      ...prev,
      [rowId]: {
        stake1: newStake1,
        stake2: newStake2,
      }
    }));
  };

  // Reset stakes for a specific arbitrage
  const resetStakes = (rowId) => {
    setManualStakes(prev => {
      const newStakes = { ...prev };
      delete newStakes[rowId];
      return newStakes;
    });
  };

  // Settings handlers
  const handleSettingsOpen = useCallback((event) => {
    setSettingsAnchorEl(event.currentTarget);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  // Compute filter options and filtered/sorted arbitrages
  const { filterOptions, filteredArbitrages } = useMemo(() => {
    if (!arbitrages || !arbitrages.length) {
      return {
        filterOptions: { leagues: [], books: [] },
        filteredArbitrages: [],
      };
    }

    // Build filter options
    const leagues = new Set();
    const books = new Set();
    arbitrages.forEach((arb) => {
      if (arb.name) leagues.add(arb.name);
      if (arb.ms1) books.add(arb.ms1);
      if (arb.ms2) books.add(arb.ms2);
    });

    // Apply filters
    const leagueSet = new Set(leagueFilter);
    const bookSet = new Set(bookFilter);
    const trackedSet = new Set(trackedBets.map(bet => bet.oddsId || bet.id));
    const hiddenSet = new Set(hiddenArbs);
    const filtered = arbitrages.filter((arb) => {
      // Calculate the arbitrage ROI to exclude unrealistic opportunities
      const totalStake = arb.stake1 + arb.stake2;
      const arbProfit = arb.guaranteedProfit;
      const arbROI = totalStake > 0 ? (arbProfit / totalStake) : 0;
      
      // Exclude arbitrages with ROI > 10% (likely stale or errors)
      if (arbROI > 0.05) {
        return false;
      }
      
      if (leagueFilter.length > 0 && arb.name && !leagueSet.has(arb.name)) {
        return false;
      }
      if (bookFilter.length > 0) {
        const matchesBook1 = arb.ms1 && bookSet.has(arb.ms1);
        const matchesBook2 = arb.ms2 && bookSet.has(arb.ms2);
        if (!matchesBook1 || !matchesBook2) {
          return false;
        }
      }
      // Hide arbitrages where both bets are tracked
      if (hideTracked) {
        const bet1Tracked = trackedSet.has(arb.oddsId1);
        const bet2Tracked = trackedSet.has(arb.oddsId2);
        if (bet1Tracked && bet2Tracked) {
          return false;
        }
      }
      // Hide hidden arbitrages if showHiddenArbs is false
      if (!showHiddenArbs && hiddenSet.has(arb.id)) {
        return false;
      }
      return true;
    });

    // Adjust stakes and guaranteedProfit based on defaultStake or manual overrides
    const baseTotalStake = 100;
    const scaleFactor = defaultStake / baseTotalStake;
    const adjusted = filtered.map(arb => {
      let stake1, stake2;
      
      // Check if there are manual stakes for this arbitrage
      if (manualStakes[arb.id]) {
        stake1 = manualStakes[arb.id].stake1;
        stake2 = manualStakes[arb.id].stake2;
      } else {
        // Use default calculation
        stake1 = Math.round(arb.stake1 * scaleFactor * 100) / 100;
        stake2 = Math.round(arb.stake2 * scaleFactor * 100) / 100;
      }

      const actualProfit = calculateProfit(stake1, stake2, arb.price1, arb.price2);

      return {
        ...arb,
        stake1,
        stake2,
        actualProfit,
        hasManualStakes: !!manualStakes[arb.id],
      };
    });

    // Sort by ROI (actualProfit / totalStake) descending
    const sorted = [...adjusted].sort((a, b) => {
      const roiA = (a.stake1 + a.stake2) > 0 ? (a.actualProfit / (a.stake1 + a.stake2)) : 0;
      const roiB = (b.stake1 + b.stake2) > 0 ? (b.actualProfit / (b.stake1 + b.stake2)) : 0;
      return roiB - roiA;
    });

    // Separate pinned and unpinned items, maintaining pinned order
    const pinnedSet = new Set(pinnedArbs.filter(item => typeof item === 'string'));
    const arbsWithKeys = sorted.map(arb => ({ arb, key: createPinKey(arb) }));
    
    // Get pinned items in the order they were pinned
    const pinnedItems = pinnedArbs
      .filter(item => typeof item === 'string')
      .map(pinKey => arbsWithKeys.find(({ key }) => key === pinKey))
      .filter(Boolean)
      .map(({ arb }) => arb);
    
    // Get unpinned items (already sorted by ROI)
    const unpinnedItems = arbsWithKeys
      .filter(({ key }) => !pinnedSet.has(key))
      .map(({ arb }) => arb);

    // Combine with pinned items first, preserving their pinned order
    const finalArbitrages = [...pinnedItems, ...unpinnedItems];

    return {
      filterOptions: {
        leagues: Array.from(leagues),
        books: Array.from(books),
      },
      filteredArbitrages: finalArbitrages,
    };
  }, [arbitrages, leagueFilter, bookFilter, defaultStake, manualStakes, hideTracked, trackedBets, hiddenArbs, showHiddenArbs, pinnedArbs, createPinKey]);

  // Initialize filters only once when options are available
  useEffect(() => {
    if (filterOptions.leagues.length > 0 && !leagueInitialized.current) {
      // If we have saved filters, validate them against current options
      if (savedLeagueFilter.length > 0) {
        const validSavedFilters = savedLeagueFilter.filter(league => 
          filterOptions.leagues.includes(league)
        );
        if (validSavedFilters.length > 0) {
          setLeagueFilter(validSavedFilters);
        } else {
          setLeagueFilter(filterOptions.leagues);
        }
      } else {
        setLeagueFilter(filterOptions.leagues);
      }
      leagueInitialized.current = true;
    }
  }, [filterOptions.leagues, savedLeagueFilter]);

  useEffect(() => {
    if (filterOptions.books.length > 0 && !bookInitialized.current) {
      // If we have saved filters, validate them against current options
      if (savedBookFilter.length > 0) {
        const validSavedFilters = savedBookFilter.filter(book => 
          filterOptions.books.includes(book)
        );
        // Ensure at least 2 books are selected
        if (validSavedFilters.length >= 2) {
          setBookFilter(validSavedFilters);
        } else if (validSavedFilters.length === 1) {
          // Add one more book if only one valid saved filter
          const additionalBook = filterOptions.books.find(book => 
            !validSavedFilters.includes(book)
          );
          setBookFilter([...validSavedFilters, additionalBook]);
        } else {
          // Default to first 2 books if no valid saved filters
          setBookFilter(filterOptions.books.slice(0, 2));
        }
      } else {
        setBookFilter(filterOptions.books);
      }
      bookInitialized.current = true;
    }
  }, [filterOptions.books, savedBookFilter]);

  // Filter handlers with minimum book selection enforcement
  const handleFilterChange = (e, options, setter, type) => {
    const val = e.target.value;
    if (type === 'book' && val.length < 2) {
      if (val.length === 0) {
        setter(options.slice(0, 2));
      } else if (val.length === 1) {
        const remainingOptions = options.filter(opt => !val.includes(opt));
        setter([...val, remainingOptions[0]]);
      }
      return;
    }

    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        if (type === 'book' && newValue.length < 2) {
          const enforcedValue = options.slice(0, 2);
          return enforcedValue;
        }
        return newValue;
      });
    } else {
      setter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  // Handle default stake input change
  const handleDefaultStakeChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setDefaultStake('');
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setDefaultStake(Math.max(1, numValue));
      }
    }
  };

  // Handle blur to ensure minimum stake
  const handleDefaultStakeBlur = () => {
    if (defaultStake === '' || defaultStake < 1) {
      setDefaultStake(1);
    }
  };

  // Mobile pagination
  const mobilePageSize = 5;
  const totalPages = Math.ceil(filteredArbitrages.length / mobilePageSize);
  const startIdx = currentPage * mobilePageSize;
  const endIdx = startIdx + mobilePageSize;
  const paginatedArbitrages = filteredArbitrages.slice(startIdx, endIdx);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [leagueFilter, bookFilter, hideTracked, showHiddenArbs]);

  // Define columns with stacked Book 1 and Book 2 data, ensuring consistent alignment
  const columns = [
    {
      field: 'pinArb',
      headerName: '',
      sortable: false,
      minWidth: 50,
      maxWidth: 50,
      flex: 0,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <PinArbCell
          row={params.row}
          pinnedArbs={pinnedArbs}
          togglePin={togglePin}
          createPinKey={createPinKey}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'eventShortName',
      headerName: 'Event',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.name || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {params.row.awayLogo && (
                <img
                  src={params.row.awayLogo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {params.row.homeLogo && (
                <img
                  src={params.row.homeLogo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '14px',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '14px',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'logo1',
      headerName: 'Book',
      minWidth: 90,
      flex: 0.1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '50%',
              paddingTop: '2px',
            }}
          >
            <img
              src={params.value}
              alt="Logo 1"
              style={{ width: 72, height: 36, objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '50%',
              paddingBottom: '2px',
            }}
          >
            <img
              src={params.row.logo2}
              alt="Logo 2"
              style={{ width: 72, height: 36, objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
        </Box>
      ),
    },
    {
      field: 'lineOdds',
      headerName: 'Line/Odds',
      minWidth: 130,
      flex: 1,
      sortable: false,
      headerAlign: 'left',
      align: 'right',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingRight: '8px',
          }}
        >
          {/* Book 1 */}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {`${params.row.side1 === 'Over' ? 'o' : params.row.side1 === 'Under' ? 'u' : ''} ${params.row.points1 || ''}`.trim()}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {params.row.price1 < 0 ? params.row.price1 : `+${params.row.price1}`}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Book 2 */}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {`${params.row.side2 === 'Over' ? 'o' : params.row.side2 === 'Under' ? 'u' : ''} ${params.row.points1 || ''}`.trim()}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {params.row.price2 < 0 ? params.row.price2 : `+${params.row.price2}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'stake1',
      headerName: 'Stake',
      minWidth: 110,
      flex: 1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {params.row.hasManualStakes && (
            <Tooltip title="Reset to default stakes">
              <Button
                size="small"
                onClick={() => resetStakes(params.row.id)}
                sx={{
                  position: 'absolute',
                  top: '2px',
                  right: '10px',
                  minWidth: '20px',
                  padding: '2px',
                  fontSize: '10px',
                  color: '#ff1744',
                  '&:hover': { backgroundColor: 'rgba(255, 23, 68, 0.08)' }
                }}
              >
                ↺
              </Button>
            </Tooltip>
          )}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <EditableStakeCell
              value={params.value}
              rowId={params.row.id}
              field="stake1"
              onStakeChange={handleStakeChange}
            />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <EditableStakeCell
              value={params.row.stake2}
              rowId={params.row.id}
              field="stake2"
              onStakeChange={handleStakeChange}
            />
          </Box>
        </Box>
      ),
    },
    {
      field: 'actualProfit',
      headerName: 'Profit',
      minWidth: 125,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      sortComparator: (v1, v2, param1, param2) => {
        // Sort by ROI instead of profit amount
        const totalStake1 = param1.row.stake1 + param1.row.stake2;
        const totalStake2 = param2.row.stake1 + param2.row.stake2;
        const roi1 = totalStake1 > 0 ? (v1 / totalStake1) : 0;
        const roi2 = totalStake2 > 0 ? (v2 / totalStake2) : 0;
        return roi1 - roi2;
      },
      renderCell: (params) => {
        const totalStake = params.row.stake1 + params.row.stake2;
        const roi = totalStake > 0 ? (params.value / totalStake) * 100 : 0;
        
        // Conditional formatting for ROI
        let roiColor = '#ffffff';
        if (roi >= 10) {
          roiColor = '#00ff00'; // Bright green for 10%+ ROI
        } else if (roi >= 5) {
          roiColor = '#66ff66'; // Medium green for 5-10% ROI
        } else if (roi >= 2) {
          roiColor = '#99ff99'; // Light green for 2-5% ROI
        } else if (roi >= 1) {
          roiColor = '#ccffcc'; // Very light green for 1-2% ROI
        }
        
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            paddingLeft: '8px'
          }}>
            <Typography variant="body2" sx={{ color: '#ffffff', lineHeight: 1.5 }}>
              ${params.value.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: roiColor, fontSize: '0.875rem', lineHeight: 'normal' }}>
              {roi.toFixed(1)}% ROI
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'trackArb',
      headerName: 'Track',
      sortable: false,
      minWidth: 65,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <TrackArbCell
          row={params.row}
          trackedBets={trackedBets}
          toggleTrack={toggleTrack}
          trackBet={trackBet}
          untrackBet={untrackBet}
          trackingInProgress={trackingInProgress}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'hideArb',
      headerName: 'Hide',
      sortable: false,
      minWidth: 65,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <HideArbCell
          row={params.row}
          hiddenArbs={hiddenArbs}
          toggleHide={toggleHide}
          showHiddenArbs={showHiddenArbs}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'deeplink1',
      headerName: '',
      sortable: false,
      minWidth: 80,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell 
              marketLineId={params.row.marketLineId1}
              points={params.row.points1}
              marketSourceName={params.row.ms1}
              fetchDeeplink={fetchDeeplink}
              isAuthenticated={isAuthenticated}
            />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell 
              marketLineId={params.row.marketLineId2}
              points={params.row.points1}
              marketSourceName={params.row.ms2}
              fetchDeeplink={fetchDeeplink}
              isAuthenticated={isAuthenticated}
            />
          </Box>
        </Box>
      ),
    },
  ];

  // Remove eventStart from columnVisibilityModel since it's now combined
  const columnVisibilityModel = {};

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          
          {/* Mobile View */}
          {isMobile ? (
            <>
              {/* Mobile Header */}
              <Box sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: '#121212',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #333',
                zIndex: 10
              }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple
                        labelId="mobile-league-filter-label"
                        label="League"
                        notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        sx={{ fontSize: '0.875rem' }}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={leagueFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple
                        labelId="mobile-book-filter-label"
                        label="Book"
                        notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        sx={{ fontSize: '0.875rem' }}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={bookFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton 
                      onClick={handleSettingsOpen} 
                      disabled={!isAuthenticated}
                      sx={{ 
                        color: '#ffffff', 
                        '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 },
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Default Stake"
                    type="number"
                    size="small"
                    value={defaultStake}
                    onChange={handleDefaultStakeChange}
                    onBlur={handleDefaultStakeBlur}
                    InputProps={{
                      startAdornment: <Typography sx={{ color: '#ffffff', mr: 1 }}>$</Typography>,
                      inputProps: { min: 1, step: 1 },
                    }}
                    sx={{ width: '100%' }}
                    disabled={!isAuthenticated}
                  />
                </Stack>
              </Box>

              {/* Mobile Content */}
              <Box sx={{ 
                flex: 1,
                overflowY: 'auto',
                p: 2,
                paddingBottom: totalPages > 1 ? '80px' : '16px'
              }}>
                {filteredArbitrages.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                      No arbitrages available, please check back later
                    </Typography>
                  </Box>
                ) : (
                  paginatedArbitrages.map(arb => (
                    <MobileArbCard 
                      key={arb.id} 
                      arb={arb} 
                      defaultStake={defaultStake}
                      onStakeChange={handleStakeChange}
                      trackedBets={trackedBets}
                      trackBet={trackBet}
                      untrackBet={untrackBet}
                      trackingInProgress={trackingInProgress}
                      isAuthenticated={isAuthenticated}
                      hiddenArbs={hiddenArbs}
                      toggleHide={toggleHide}
                      showHiddenArbs={showHiddenArbs}
                      pinnedArbs={pinnedArbs}
                      togglePin={togglePin}
                      createPinKey={createPinKey}
                      fetchDeeplink={fetchDeeplink}
                    />
                  ))
                )}
              </Box>

              {/* Mobile Pagination */}
              {totalPages > 1 && (
                <Box sx={{ 
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: '#1e1e1e',
                  borderTop: '1px solid #333',
                  p: 2,
                  zIndex: 9
                }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0 || !isAuthenticated}
                      sx={{ color: '#ffffff' }}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      {currentPage + 1} / {totalPages}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage >= totalPages - 1 || !isAuthenticated}
                      sx={{ color: '#ffffff' }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            // Desktop View
            <>
              <Box className="filter-section">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple
                        labelId="league-filter-label"
                        label="League"
                        notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={leagueFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple
                        labelId="book-filter-label"
                        label="Book"
                        notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={bookFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Default Stake"
                      type="number"
                      size="small"
                      value={defaultStake}
                      onChange={handleDefaultStakeChange}
                      onBlur={handleDefaultStakeBlur}
                      InputProps={{
                        startAdornment: <Typography sx={{ color: '#ffffff', mr: 1 }}>$</Typography>,
                        inputProps: { min: 1, step: 1 },
                      }}
                      sx={{ width: 150 }}
                      disabled={!isAuthenticated}
                    />
                  </Stack>
                  <IconButton 
                    onClick={handleSettingsOpen} 
                    disabled={!isAuthenticated}
                    sx={{ 
                      color: '#ffffff', 
                      '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
              </Box>
              <Box className="table-section">
                <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  {filteredArbitrages.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                        No arbitrages available, please check back later
                      </Typography>
                    </Box>
                  ) : (
                    <DataGridPro
                      rows={filteredArbitrages}
                      columns={columns}
                      getRowId={row => row.id}
                      getRowClassName={(params) => {
                        const pinKey = createPinKey(params.row);
                        return pinnedArbs.includes(pinKey) ? 'pinned-row' : '';
                      }}
                      sortingMode="client"
                      disableSelectionOnClick
                      hideFooter
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e',
                          borderBottom: '2px solid #444',
                          position: 'sticky',
                          top: '56px',
                          zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                        },
                        '& .MuiDataGrid-cell:last-child': {
                          borderRight: 'none', // Remove border from last column
                        },
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-row': {
                          position: 'relative',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                          '&.pinned-row': {
                            backgroundColor: 'rgba(255, 23, 68, 0.08)',
                            '&:hover': { backgroundColor: 'rgba(255, 23, 68, 0.12)' },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '3px',
                              backgroundColor: '#ff1744',
                            }
                          }
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { 
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.2,
                        },
                        '& .MuiDataGrid-root': {
                          overflowY: 'auto !important',
                          margin: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          overflowY: 'auto !important',
                        },
                        '& .MuiDataGrid-main': {
                          padding: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff !important',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={5}
                      rowThreshold={2}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
<Popover
  open={Boolean(settingsAnchorEl)}
  anchorEl={settingsAnchorEl}
  onClose={handleSettingsClose}
  anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'center',
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'center',
  }}
  sx={{ zIndex: 1300 }}
>
  <Box sx={{ p: 2, minWidth: 200 }}>
    <Stack direction="column" spacing={1}>
      <FormControlLabel
        control={
          <Checkbox
            checked={hideTracked}
            onChange={(e) => setHideTracked(e.target.checked)}
            sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
            disabled={!isAuthenticated}
          />
        }
        label="Hide Tracked Bets"
        sx={{ color: '#ffffff' }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={showHiddenArbs}
            onChange={(e) => setShowHiddenArbs(e.target.checked)}
            sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
            disabled={!isAuthenticated}
          />
        }
        label="Show Hidden Arbs"
        sx={{ color: '#ffffff', mb: 2 }}
      />
    </Stack>
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button
        size="small"
        onClick={handleSettingsClose}
        sx={{ color: '#ffffff' }}
      >
        Cancel
      </Button>
      <Button
        size="small"
        onClick={handleSettingsClose}
        variant="contained"
        sx={{ backgroundColor: '#ff1744', '&:hover': { backgroundColor: '#d81b60' } }}
      >
        Save
      </Button>
    </Stack>
  </Box>
</Popover>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default ArbDataGrid;